import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';
import { fetchCurrentUser } from '../../ducks/user.duck';
import { NamedLink } from '../../components';
import { SESSION_TOPIC_LABELS, normalizeTopics } from '../../util/sessionTopics';
import { TALK_ABOUT_IDS, talkAboutPath } from '../../util/talkAboutSlugs';
import { normalizePaths, pathLabel, pathsParam, LEGACY_TO_PATH } from '../../util/topicPaths';
import css from './TopicHeart.module.css';
import BookmarkIcon from '../BookmarkIcon/BookmarkIcon';

/** Talker's favorite topics/interests: privateData.favoriteTopics (private, never shown publicly). */
export const favoriteTopicsFromUser = user =>
  normalizeTopics(user?.attributes?.profile?.privateData?.favoriteTopics);

const withTopics = (user, ids) => ({
  ...user,
  attributes: {
    ...user.attributes,
    profile: {
      ...user.attributes.profile,
      privateData: { ...(user.attributes.profile.privateData || {}), favoriteTopics: ids },
    },
  },
});

export const toggleFavoriteTopic = id => (dispatch, getState, sdk) => {
  const currentUser = getState()?.user?.currentUser;
  if (!currentUser?.id?.uuid || !sdk?.currentUser?.updateProfile) return Promise.resolve();
  const cur = favoriteTopicsFromUser(currentUser);
  const next = cur.includes(id) ? cur.filter(t => t !== id) : [...cur, id].slice(0, 40);
  dispatch({ type: 'user/setCurrentUser', payload: withTopics(currentUser, next) });
  return sdk.currentUser
    .updateProfile({ privateData: { favoriteTopics: next } }, { expand: true })
    .then(() => dispatch(fetchCurrentUser({ enforce: true, updateHasListings: false, updateNotifications: false })))
    .catch(() => dispatch({ type: 'user/setCurrentUser', payload: currentUser }));
};

/** Saved topic paths (any level): privateData.favoriteTopicPaths, plus old flat favorites mapped to paths. */
export const favoritePathsFromUser = user => {
  const pd = user?.attributes?.profile?.privateData || {};
  const legacy = favoriteTopicsFromUser(user).map(id => LEGACY_TO_PATH[id]).filter(Boolean);
  return normalizePaths([...normalizePaths(pd.favoriteTopicPaths), ...legacy]);
};

export const togglePathFavorite = path => (dispatch, getState, sdk) => {
  const currentUser = getState()?.user?.currentUser;
  if (!currentUser?.id?.uuid || !sdk?.currentUser?.updateProfile) return Promise.resolve();
  const pd = currentUser.attributes.profile.privateData || {};
  const cur = favoritePathsFromUser(currentUser);
  const removing = cur.includes(path);
  const nextPaths = removing ? cur.filter(p => p !== path) : normalizePaths([...cur, path]);
  // Unsaving a path also drops the old flat id it came from.
  const nextLegacy = favoriteTopicsFromUser(currentUser).filter(id => !(removing && LEGACY_TO_PATH[id] === path));
  const privateData = { ...pd, favoriteTopicPaths: nextPaths, favoriteTopics: nextLegacy };
  const optimistic = {
    ...currentUser,
    attributes: { ...currentUser.attributes, profile: { ...currentUser.attributes.profile, privateData } },
  };
  dispatch({ type: 'user/setCurrentUser', payload: optimistic });
  return sdk.currentUser
    .updateProfile({ privateData: { favoriteTopicPaths: nextPaths, favoriteTopics: nextLegacy } }, { expand: true })
    .then(() => dispatch(fetchCurrentUser({ enforce: true, updateHasListings: false, updateNotifications: false })))
    .catch(() => dispatch({ type: 'user/setCurrentUser', payload: currentUser }));
};

const HeartSvg = ({ on }) => <BookmarkIcon on={on} className={css.svg} />;

/** Small heart next to a topic. Logged out → sign-up prompt. */
export const TopicHeart = ({ id, className, large = false }) => {
  const dispatch = useDispatch();
  const currentUser = useSelector(state => state.user?.currentUser);
  const [busy, setBusy] = useState(false);
  const label = SESSION_TOPIC_LABELS[id] || id;
  const cls = classNames(css.heart, { [css.large]: large }, className);
  if (!currentUser?.id?.uuid) {
    return (
      <a href="/signup?role=talk&fav=1" className={cls} aria-label={`Sign up to save ${label}`} title="Sign up to save your topics" data-topic-heart="signup">
        <HeartSvg on={false} />
      </a>
    );
  }
  const on = favoriteTopicsFromUser(currentUser).includes(id);
  return (
    <button
      type="button"
      className={classNames(cls, { [css.on]: on })}
      aria-pressed={on}
      aria-label={on ? `Remove ${label} from my topics` : `Save ${label} to my topics`}
      title={on ? 'Saved' : 'Save'}
      data-topic-heart={on ? 'on' : 'off'}
      disabled={busy}
      onClick={e => {
        e.preventDefault();
        e.stopPropagation();
        setBusy(true);
        Promise.resolve(dispatch(toggleFavoriteTopic(id))).finally(() => setBusy(false));
      }}
    >
      <HeartSvg on={on} />
    </button>
  );
};

/** Heart for a topic path at any level (Browse picker, My topics). */
export const PathHeart = ({ path, className }) => {
  const dispatch = useDispatch();
  const currentUser = useSelector(state => state.user?.currentUser);
  const [busy, setBusy] = useState(false);
  const label = pathLabel(path);
  const cls = classNames(css.heart, className);
  if (!currentUser?.id?.uuid) {
    return (
      <a href="/signup?role=talk&fav=1" className={cls} aria-label={`Sign up to save ${label}`} data-path-heart="signup">
        <HeartSvg on={false} />
      </a>
    );
  }
  const on = favoritePathsFromUser(currentUser).includes(path);
  return (
    <button
      type="button"
      className={classNames(cls, { [css.on]: on })}
      aria-pressed={on}
      aria-label={on ? `Remove ${label} from my topics` : `Save ${label} to my topics`}
      data-path-heart={on ? 'on' : 'off'}
      data-heart-path={path}
      disabled={busy}
      onClick={e => {
        e.preventDefault();
        e.stopPropagation();
        setBusy(true);
        Promise.resolve(dispatch(togglePathFavorite(path))).finally(() => setBusy(false));
      }}
    >
      <HeartSvg on={on} />
    </button>
  );
};

/** Drop a topic from the talking profile (publicData.interestedTopicPaths). */
export const removeProfilePath = path => (dispatch, getState, sdk) => {
  const currentUser = getState()?.user?.currentUser;
  if (!currentUser?.id?.uuid || !sdk?.currentUser?.updateProfile) return Promise.resolve();
  const pub = currentUser.attributes.profile.publicData || {};
  const next = normalizePaths(pub.interestedTopicPaths).filter(p => p !== path);
  const optimistic = {
    ...currentUser,
    attributes: { ...currentUser.attributes, profile: { ...currentUser.attributes.profile, publicData: { ...pub, interestedTopicPaths: next } } },
  };
  dispatch({ type: 'user/setCurrentUser', payload: optimistic });
  return sdk.currentUser
    .updateProfile({ publicData: { interestedTopicPaths: next } }, { expand: true })
    .then(() => dispatch(fetchCurrentUser({ enforce: true, updateHasListings: false, updateNotifications: false })))
    .catch(() => dispatch({ type: 'user/setCurrentUser', payload: currentUser }));
};

/** Talker dashboard "Saved search topics" (Sam, Oct 20 2026): tap a chip to search; ✕ removes it. Hidden when empty. */
export const MyTopics = () => {
  const dispatch = useDispatch();
  const [busy, setBusy] = useState(null);
  const currentUser = useSelector(state => state.user?.currentUser);
  const saved = favoritePathsFromUser(currentUser);
  const fromProfile = saved.length
    ? []
    : normalizePaths(currentUser?.attributes?.profile?.publicData?.interestedTopicPaths).slice(0, 12);
  const list = saved.length ? saved : fromProfile;
  if (!list.length) return null;
  const remove = p => {
    setBusy(p);
    const action = saved.length ? togglePathFavorite(p) : removeProfilePath(p);
    Promise.resolve(dispatch(action)).finally(() => setBusy(null));
  };
  return (
    <section className={css.myTopics} data-my-topics="true">
      <h3 className={css.myTitle}>Saved search topics</h3>
      <ul className={css.chipList}>
        {list.map(p => {
          const label = pathLabel(p).split(' › ').slice(-1)[0];
          return (
            <li key={p} className={css.chip} data-saved-topic={p}>
              <NamedLink name="SearchPage" to={{ search: `?mode=topics&paths=${encodeURIComponent(pathsParam([p]))}` }} className={css.chipLink} data-my-topic-chip={p} title={pathLabel(p)}>
                {label}
              </NamedLink>
              <button type="button" className={css.chipX} aria-label={`Remove ${label} from saved search topics`} disabled={busy === p} onClick={() => remove(p)} data-saved-topic-remove={p}>
                ✕
              </button>
            </li>
          );
        })}
      </ul>
    </section>
  );
};

export default TopicHeart;
